import React, { useMemo, useState, useRef, useEffect } from "react";

import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Grid, Paper, Typography, Chip, Box, CircularProgress,
    useMediaQuery, Divider, Stack, IconButton
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";

import CloseIcon from '@mui/icons-material/Close';

import api from "../../services/api";
import { getApiErrorMessage } from "../../services/apiUtils";
import { useSnackbar } from "../../contexts/SnackbarContext";

const PLANS = [
    {
        tipo: "PROFESSOR",
        title: "Professor",
        price: 29.9,
        label: "R$29,90/mês",
        tagline: "Para menor demanda mensal",
        gains: [
            "10 gerações mensais",
            "BNCC + textos bem estruturados",
            "Exportação Word/PDF",
            "Suporte por WhatsApp"
        ],
        recommended: false
    },
    {
        tipo: "MENTOR",
        title: "Mentor",
        price: 49.9,
        label: "R$49,90/mês",
        tagline: "Para rotina mais frequente",
        gains: [
            "25 gerações mensais",
            "10 imagens com IA por mês",
            "Exportação Word/PDF + Gabarito",

            "Suporte por WhatsApp"
        ],
        recommended: false
    },
    {
        tipo: "MESTRE",
        title: "Mestre",
        price: 69.9,
        label: "R$69,90/mês",
        tagline: "Para alta demanda e volume",
        gains: [
            "50 gerações mensais",
            "20 imagens com IA por mês",
            "Exportação Word/PDF + Gabarito",

            "Suporte por WhatsApp"
        ],
        recommended: true
    }
];

const byTipo = (t) => PLANS.find(p => p.tipo === String(t || "").toUpperCase().trim()) || null;

export default function UpgradePlanDialog({ open, onClose, userId, planoAtual, onUpgraded, presetPlan, onlyPlan }) {

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const resumoRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [selecionado, setSelecionado] = useState(null);
    const { showSnackbar } = useSnackbar();

    useEffect(() => {
        if (!open) return;

        if (presetPlan) {
            setSelecionado(String(presetPlan).toUpperCase().trim());

            // já rola pro resumo ao abrir (UX)
            setTimeout(() => {
                resumoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 120);
        }
    }, [open, presetPlan]);

    const atualTipo = String(planoAtual || "").toUpperCase().trim();
    const atualPlan = useMemo(() => byTipo(atualTipo), [atualTipo]);
    const atualPrice = atualPlan?.price || 0;

    const selectedPlan = useMemo(() => byTipo(selecionado), [selecionado]);

    const handleClose = () => {
        if (loading) return;
        setSelecionado(null);
        onClose?.();
    };

    const handleConfirm = async () => {
        if (!selecionado) return;
        setLoading(true);
        try {
            await api.patch(
                "/billing/plan/upgrade",
                { planName: selecionado }
            );

            onUpgraded?.(selecionado);
            handleClose();
        } catch (err) {
            showSnackbar(getApiErrorMessage(err, "Não foi possível atualizar o plano."), "error");
        } finally {
            setLoading(false);
        }
    };

    // benefícios extras do selecionado em relação ao atual (texto simples, sem preço)
    const extras = useMemo(() => {
        if (!selectedPlan) return [];
        const cur = new Set((atualPlan?.gains || []).map(x => String(x)));
        return (selectedPlan.gains || []).filter(x => !cur.has(String(x)));
    }, [selectedPlan, atualPlan]);

    const plansToShow = useMemo(() => {
        if (!onlyPlan) return PLANS;

        // onlyPlan pode ser string ("MESTRE") ou array (["MENTOR","MESTRE"])
        const allowed = Array.isArray(onlyPlan)
            ? onlyPlan.map(x => String(x).toUpperCase().trim())
            : [String(onlyPlan).toUpperCase().trim()];

        return PLANS.filter(p => allowed.includes(p.tipo));
    }, [onlyPlan]);


    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    p: 0,
                    borderRadius: isMobile ? 0 : 3
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 900, pb: 1 }}>
                Atualização do plano
                <IconButton onClick={handleClose} sx={{ position: "absolute", right: 8, top: 10 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ pb: isMobile ? 10 : 3 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ color: "text.secondary" }}>
                        Plano atual
                    </Typography>
                    <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Chip
                            label={atualPlan ? `${atualPlan.title} • ${atualPlan.label}` : (planoAtual || "-")}
                            sx={{ fontWeight: 800 }}
                        />
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            Apenas upgrades são permitidos.
                        </Typography>
                    </Box>
                </Box>

                <Grid container spacing={2}>
                    {plansToShow.map((p) => {
                        const isUpgrade = p.price > atualPrice;
                        const disabled = !isUpgrade;
                        const isSelected = selecionado === p.tipo;
                        const isMestre = p.tipo === "MESTRE";

                        const mainColor = isMestre ? theme.palette.secondary.main : theme.palette.primary.main;

                        return (
                            <Grid item xs={12} sm={4} key={p.tipo}>
                                <Paper
                                    onClick={() => {
                                        if (disabled) return;
                                        setSelecionado(p.tipo);

                                        // rola até o resumo (leve delay para garantir render)
                                        setTimeout(() => {
                                            resumoRef.current?.scrollIntoView({
                                                behavior: "smooth",
                                                block: "start",
                                            });
                                        }, 80);
                                    }}
                                    sx={{
                                        p: 2.25,
                                        borderRadius: 3,
                                        cursor: disabled ? "not-allowed" : "pointer",
                                        opacity: disabled ? 0.45 : 1,
                                        border: isSelected
                                            ? `2px solid ${mainColor}`
                                            : `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                                        background: isSelected ? alpha(mainColor, 0.10) : "transparent",
                                        transition: "all 160ms ease",
                                        "&:hover": disabled ? {} : { borderColor: mainColor, transform: "translateY(-2px)" }
                                    }}
                                >
                                    {/* Header */}
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                        <Typography fontWeight={900}>{p.title}</Typography>

                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            {p.recommended ? (
                                                <Chip
                                                    size="small"
                                                    icon={<WorkspacePremiumRoundedIcon />}
                                                    label="Recomendado"
                                                    color="secondary"
                                                    sx={{ fontWeight: 900 }}
                                                />
                                            ) : null}

                                            {isSelected ? <CheckCircleRoundedIcon sx={{ color: mainColor }} /> : null}
                                        </Box>
                                    </Box>

                                    <Typography sx={{ mt: 0.8, fontWeight: 900, fontSize: 18 }}>
                                        {p.label}
                                    </Typography>

                                    <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
                                        {p.tagline}
                                    </Typography>

                                    <Divider sx={{ my: 1.2 }} />

                                    {/* Benefícios (sem foco em preço/diferença) */}
                                    <Typography variant="caption" sx={{ fontWeight: 900, color: "text.secondary" }}>
                                        O que você ganha:
                                    </Typography>

                                    <Stack spacing={0.75} sx={{ mt: 1 }}>
                                        {p.gains.slice(0, 4).map((g) => (
                                            <Typography key={g} variant="body2" sx={{ color: "text.secondary" }}>
                                                • {g}
                                            </Typography>
                                        ))}
                                    </Stack>

                                    <Box sx={{ mt: 1.5 }}>
                                        {disabled ? (
                                            <Chip size="small" label="Seu plano atual" />
                                        ) : (
                                            <Chip size="small" label="Upgrade disponível" sx={{ fontWeight: 800 }} />
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Resumo: somente benefícios extras (sem falar de valor a mais) */}
                <Box ref={resumoRef} sx={{ mt: 2.5 }}>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            borderRadius: 3,
                            borderColor: selecionado ? alpha(theme.palette.primary.main, 0.35) : alpha(theme.palette.divider, 0.7),
                            bgcolor: selecionado ? alpha(theme.palette.primary.main, 0.04) : "transparent"
                        }}
                    >
                        <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                            {selecionado ? "O que muda com esse upgrade" : "Selecione um plano para ver o resumo"}
                        </Typography>

                        {selecionado ? (
                            <>
                                <Typography sx={{ color: "text.secondary" }}>
                                    {atualPlan?.title || "Atual"}{" "}
                                    <ArrowForwardRoundedIcon sx={{ fontSize: 16, verticalAlign: "middle", mx: 0.5 }} />
                                    <b>{selectedPlan?.title}</b>
                                </Typography>

                                <Box sx={{ mt: 1 }}>
                                    {extras.length ? (
                                        extras.slice(0, 6).map((e) => (
                                            <Typography key={e} variant="body2" sx={{ color: "text.secondary" }}>
                                                • {e}
                                            </Typography>
                                        ))
                                    ) : (
                                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                            Você mantém os recursos atuais e adiciona benefícios do novo plano.
                                        </Typography>
                                    )}
                                </Box>

                                <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
                                    A atualização vale para as próximas cobranças (e pendências podem ser atualizadas automaticamente).
                                </Typography>
                            </>
                        ) : null}
                    </Paper>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    p: 2,
                    position: isMobile ? "fixed" : "static",
                    bottom: isMobile ? 0 : "auto",
                    left: isMobile ? 0 : "auto",
                    right: isMobile ? 0 : "auto",
                    bgcolor: isMobile ? "background.paper" : "transparent",
                    borderTop: isMobile ? `1px solid ${theme.palette.divider}` : "none",
                    zIndex: 10
                }}
            >
                <Button onClick={handleClose} disabled={loading} sx={{ fontWeight: 900, textTransform: "none" }}>
                    Fechar
                </Button>

                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={loading || !selecionado}
                    sx={{ fontWeight: 900, textTransform: "none" }}
                >
                    {loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Confirmar upgrade"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
